'use client';

import type {
  MysteryRoomInventoryItemState,
  MysteryRoomLocalRuntime,
} from '../core/localRuntime';
import {
  rectContainsPoint,
  rectToSegments,
  type MysteryRoomCustomObjectDefinition,
  type MysteryRoomRect,
  type MysteryRoomResourceKind,
  type MysteryRoomSegment,
  type MysteryRoomVec2,
} from '../world/mapTypes';
import {
  MYSTERY_ROOM_RESOURCE_DEFINITIONS,
  MYSTERY_ROOM_RESOURCE_KINDS,
  mysteryRoomResourceItemId,
} from '../world/resourceDefinitions';

export type MysteryRoomBuildableKind = 'barricade' | 'resource_crate';
export type MysteryRoomResourceCounts = Record<MysteryRoomResourceKind, number>;

export const MYSTERY_ROOM_BUILD_RECIPES: Record<
  MysteryRoomBuildableKind,
  {
    label: string;
    icon: string;
    cost: Partial<Record<MysteryRoomResourceKind, number>>;
  }
> = {
  barricade: {
    label: 'Баррикада',
    icon: 'BR',
    cost: { wood: 3, metal: 1 },
  },
  resource_crate: {
    label: 'Ящик ресурсов',
    icon: 'BX',
    cost: { wood: 2, metal: 1, stone: 1 },
  },
};

type BuildPlacementResult =
  | { ok: true; rect: MysteryRoomRect }
  | { ok: false; reason: string };

export function getMysteryRoomResourceCounts(runtime: MysteryRoomLocalRuntime): MysteryRoomResourceCounts {
  const counts = emptyResourceCounts();
  for (const kind of MYSTERY_ROOM_RESOURCE_KINDS) {
    const item = runtime.inventory.find((entry) => entry.id === mysteryRoomResourceItemId(kind));
    counts[kind] = Math.max(0, Math.floor(item?.quantity ?? 0));
  }
  return counts;
}

export function getMysteryRoomBuildState(runtime: MysteryRoomLocalRuntime, kind: MysteryRoomBuildableKind) {
  const recipe = MYSTERY_ROOM_BUILD_RECIPES[kind];
  const resources = getMysteryRoomResourceCounts(runtime);
  const missing = missingBuildResources(resources, recipe.cost);
  if (missing.length > 0) {
    return {
      canBuild: false,
      reason: `Не хватает: ${missing.join(', ')}`,
      resources,
      recipe,
    };
  }
  const placement = resolveBuildPlacement(runtime, kind);
  return {
    canBuild: placement.ok,
    reason: placement.ok === false ? placement.reason : 'Можно строить',
    resources,
    recipe,
  };
}

export function canBuildMysteryRoomObject(runtime: MysteryRoomLocalRuntime, kind: MysteryRoomBuildableKind): boolean {
  return getMysteryRoomBuildState(runtime, kind).canBuild;
}

export function buildMysteryRoomObject(runtime: MysteryRoomLocalRuntime, kind: MysteryRoomBuildableKind): MysteryRoomLocalRuntime {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return runtime;
  }

  const state = getMysteryRoomBuildState(runtime, kind);
  if (!state.canBuild) {
    return appendBuildEvent(runtime, `Стройка: ${state.recipe.label} — ${state.reason.toLowerCase()}`);
  }

  const placement = resolveBuildPlacement(runtime, kind);
  if (placement.ok === false) {
    return appendBuildEvent(runtime, `Стройка: ${state.recipe.label} — ${placement.reason.toLowerCase()}`);
  }

  const object = createBuiltCustomObject(runtime, kind, placement.rect);
  const nextInventory = deductBuildResources(runtime.inventory, state.recipe.cost);
  const itemStillActive = nextInventory.some((entry) => entry.id === runtime.activeInventoryItemId && (entry.quantity ?? 0) > 0);

  return {
    ...runtime,
    map: {
      ...runtime.map,
      customObjects: [...runtime.map.customObjects, object],
    },
    inventory: nextInventory,
    activeInventoryItemId: itemStillActive ? runtime.activeInventoryItemId : null,
    events: [`Построено: ${state.recipe.label}`, ...runtime.events].slice(0, 10),
  };
}

function emptyResourceCounts(): MysteryRoomResourceCounts {
  return { wood: 0, metal: 0, stone: 0 };
}

function missingBuildResources(
  resources: MysteryRoomResourceCounts,
  cost: Partial<Record<MysteryRoomResourceKind, number>>,
): string[] {
  return MYSTERY_ROOM_RESOURCE_KINDS
    .filter((kind) => (resources[kind] ?? 0) < (cost[kind] ?? 0))
    .map((kind) => `${MYSTERY_ROOM_RESOURCE_DEFINITIONS[kind].label} ${resources[kind] ?? 0}/${cost[kind] ?? 0}`);
}

function deductBuildResources(
  inventory: MysteryRoomInventoryItemState[],
  cost: Partial<Record<MysteryRoomResourceKind, number>>,
): MysteryRoomInventoryItemState[] {
  const costById = new Map(
    MYSTERY_ROOM_RESOURCE_KINDS.map((kind) => [mysteryRoomResourceItemId(kind), cost[kind] ?? 0]),
  );
  return inventory
    .map((item) => {
      const amount = costById.get(item.id) ?? 0;
      return amount > 0 ? { ...item, quantity: Math.max(0, (item.quantity ?? 0) - amount) } : item;
    })
    .filter((item) => (item.quantity ?? 0) > 0)
    .map((item, slotIndex) => ({ ...item, slotIndex }));
}

function resolveBuildPlacement(runtime: MysteryRoomLocalRuntime, kind: MysteryRoomBuildableKind): BuildPlacementResult {
  const candidates = kind === 'barricade'
    ? [rectInHeroDirection(runtime, buildableSize(runtime, kind), runtime.hero.facing)]
    : [
        rectInHeroDirection(runtime, buildableSize(runtime, kind), runtime.hero.facing),
        rectInHeroDirection(runtime, buildableSize(runtime, kind), rotateFacing(runtime.hero.facing, 1)),
        rectInHeroDirection(runtime, buildableSize(runtime, kind), rotateFacing(runtime.hero.facing, -1)),
        rectInHeroDirection(runtime, buildableSize(runtime, kind), oppositeFacing(runtime.hero.facing)),
      ];

  let lastReason = 'нет свободного места';
  for (const rect of candidates) {
    const validation = validateBuildRect(runtime, rect);
    if (validation.ok) {
      return { ok: true, rect };
    }
    if (validation.ok === false) {
      lastReason = validation.reason;
    }
  }
  return { ok: false, reason: lastReason };
}

function buildableSize(runtime: MysteryRoomLocalRuntime, kind: MysteryRoomBuildableKind) {
  if (kind === 'resource_crate') {
    return { width: 76, height: 64 };
  }
  const horizontal = runtime.hero.facing === 'up' || runtime.hero.facing === 'down';
  return horizontal ? { width: 132, height: 34 } : { width: 34, height: 132 };
}

function rectInHeroDirection(
  runtime: MysteryRoomLocalRuntime,
  size: { width: number; height: number },
  facing: MysteryRoomLocalRuntime['hero']['facing'],
): MysteryRoomRect {
  const offset = runtime.hero.radius + 34;
  const center = { ...runtime.hero.position };
  if (facing === 'up') center.y -= offset + size.height / 2;
  if (facing === 'down') center.y += offset + size.height / 2;
  if (facing === 'left') center.x -= offset + size.width / 2;
  if (facing === 'right') center.x += offset + size.width / 2;
  return {
    x: Math.round(center.x - size.width / 2),
    y: Math.round(center.y - size.height / 2),
    width: size.width,
    height: size.height,
  };
}

function validateBuildRect(runtime: MysteryRoomLocalRuntime, rect: MysteryRoomRect): BuildPlacementResult {
  const room = runtime.map.rooms.find((entry) => entry.id === runtime.hero.zoneId);
  if (!room || !room.floor.some((floor) => rectInsideFloor(rect, floor))) {
    return { ok: false, reason: 'точка вне текущей комнаты' };
  }

  if (runtime.map.doors.some((door) => rectsOverlap(rect, door.panel) || rectsOverlap(rect, door.trigger))) {
    return { ok: false, reason: 'место пересекает дверь' };
  }

  if (runtime.map.walls.some((wall) => segmentIntersectsRect(wall, rect))) {
    return { ok: false, reason: 'место пересекает стену' };
  }

  const collidesWithObject = runtime.map.customObjects.some((object) => {
    if (object.collisionEnabled === false || object.collision.kind === 'none') return false;
    return rectsOverlap(rect, collisionBounds(object));
  });
  if (collidesWithObject) {
    return { ok: false, reason: 'место занято объектом' };
  }

  const heroRect = {
    x: runtime.hero.position.x - runtime.hero.radius,
    y: runtime.hero.position.y - runtime.hero.radius,
    width: runtime.hero.radius * 2,
    height: runtime.hero.radius * 2,
  };
  if (rectsOverlap(rect, heroRect)) {
    return { ok: false, reason: 'слишком близко к герою' };
  }

  return { ok: true, rect };
}

function createBuiltCustomObject(
  runtime: MysteryRoomLocalRuntime,
  kind: MysteryRoomBuildableKind,
  rect: MysteryRoomRect,
): MysteryRoomCustomObjectDefinition {
  const recipe = MYSTERY_ROOM_BUILD_RECIPES[kind];
  const blocksSight = kind === 'barricade';
  return {
    id: nextBuildObjectId(runtime, kind),
    kind: kind === 'barricade' ? 'wall' : 'equipment',
    zoneId: runtime.hero.zoneId,
    rect,
    rotation: 0,
    color: kind === 'barricade' ? 0x7c2d12 : 0x475569,
    colorEnabled: true,
    colorOpacity: 0.72,
    outlineColor: kind === 'barricade' ? 0xfbbf24 : 0x93c5fd,
    outlineEnabled: true,
    outlineOpacity: 0.72,
    outlineWidth: 2,
    shape: { kind: 'rect' },
    layer: 4,
    accentColor: kind === 'barricade' ? 0xfacc15 : 0xcbd5e1,
    label: recipe.label,
    collision: { kind: 'rect', rect: { ...rect }, blocksMovement: true, blocksSight },
    collisionEnabled: true,
    physicsEnabled: false,
    weightKg: kind === 'barricade' ? 70 : 38,
    blocksSight,
    visibleThroughWalls: false,
    interactive: false,
    tags: ['building', kind],
  };
}

function nextBuildObjectId(runtime: MysteryRoomLocalRuntime, kind: MysteryRoomBuildableKind): string {
  const prefix = `built_${kind}`;
  const usedIds = new Set(runtime.map.customObjects.map((entry) => entry.id));
  let index = runtime.map.customObjects.length + 1;
  let id = `${prefix}_${Math.max(0, Math.floor(runtime.elapsedMs))}_${index}`;
  while (usedIds.has(id)) {
    index += 1;
    id = `${prefix}_${Math.max(0, Math.floor(runtime.elapsedMs))}_${index}`;
  }
  return id;
}

function rectInsideFloor(rect: MysteryRoomRect, floor: MysteryRoomRect): boolean {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 },
  ].every((point) => rectContainsPoint(floor, point));
}

function collisionBounds(object: MysteryRoomCustomObjectDefinition): MysteryRoomRect {
  const collision = object.collision;
  if (collision.kind === 'rect') return collision.rect;
  if (collision.kind === 'circle') {
    return {
      x: collision.center.x - collision.radius,
      y: collision.center.y - collision.radius,
      width: collision.radius * 2,
      height: collision.radius * 2,
    };
  }
  if (collision.kind === 'polygon' && collision.points.length > 0) {
    const xs = collision.points.map((point) => point.x);
    const ys = collision.points.map((point) => point.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  return object.rect;
}

function rectsOverlap(a: MysteryRoomRect, b: MysteryRoomRect): boolean {
  return a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y;
}

function segmentIntersectsRect(segment: MysteryRoomSegment, rect: MysteryRoomRect): boolean {
  if (rectContainsPoint(rect, segment.start) || rectContainsPoint(rect, segment.end)) {
    return true;
  }
  return rectToSegments(rect).some((edge) => segmentsIntersect(segment.start, segment.end, edge.start, edge.end));
}

function segmentsIntersect(a: MysteryRoomVec2, b: MysteryRoomVec2, c: MysteryRoomVec2, d: MysteryRoomVec2): boolean {
  const orientation = (p: MysteryRoomVec2, q: MysteryRoomVec2, r: MysteryRoomVec2) =>
    (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
  const onSegment = (p: MysteryRoomVec2, q: MysteryRoomVec2, r: MysteryRoomVec2) =>
    q.x <= Math.max(p.x, r.x) + 0.001 &&
    q.x + 0.001 >= Math.min(p.x, r.x) &&
    q.y <= Math.max(p.y, r.y) + 0.001 &&
    q.y + 0.001 >= Math.min(p.y, r.y);
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);
  if (o1 * o2 < 0 && o3 * o4 < 0) return true;
  if (Math.abs(o1) < 0.001 && onSegment(a, c, b)) return true;
  if (Math.abs(o2) < 0.001 && onSegment(a, d, b)) return true;
  if (Math.abs(o3) < 0.001 && onSegment(c, a, d)) return true;
  if (Math.abs(o4) < 0.001 && onSegment(c, b, d)) return true;
  return false;
}

function rotateFacing(
  facing: MysteryRoomLocalRuntime['hero']['facing'],
  direction: 1 | -1,
): MysteryRoomLocalRuntime['hero']['facing'] {
  const order: Array<MysteryRoomLocalRuntime['hero']['facing']> = ['up', 'right', 'down', 'left'];
  const index = order.indexOf(facing);
  return order[(index + direction + order.length) % order.length];
}

function oppositeFacing(facing: MysteryRoomLocalRuntime['hero']['facing']): MysteryRoomLocalRuntime['hero']['facing'] {
  if (facing === 'up') return 'down';
  if (facing === 'down') return 'up';
  if (facing === 'left') return 'right';
  return 'left';
}

function appendBuildEvent(runtime: MysteryRoomLocalRuntime, message: string): MysteryRoomLocalRuntime {
  return {
    ...runtime,
    events: [message, ...runtime.events].slice(0, 10),
  };
}
